import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysStatusStates,
} from '../../provider/context';
import type { BaseEmptyStateProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

function DefaultEmptyIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    >
      <rect width="16" height="12" x="4" y="6" rx="2" />
      <path d="M8 10h8" />
      <path d="M8 14h5" />
    </svg>
  );
}

export const BaseEmptyState = forwardRef<HTMLDivElement, BaseEmptyStateProps>(
  function BaseEmptyState(
    {
      icon = <DefaultEmptyIcon />,
      title,
      description,
      actions,
      variant,
      appearance,
      className,
      classNames,
      style,
      ...stateProps
    },
    ref,
  ) {
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const statusStates = useMonkeysStatusStates();
    const resolvedVariant = variant ?? statusStates.empty?.variant ?? 'default';
    const densityClassName =
      statusStates.density === 'compact'
        ? 'min-h-0 px-3 py-3'
        : statusStates.density === 'comfortable'
          ? 'min-h-56 px-8 py-12'
          : undefined;
    const theme = resolveBaseAppearance(resolvedAppearance);

    return (
      <div
        {...componentAttributes}
        {...stateProps}
        ref={ref}
        data-monkeys-component="empty-state"
        data-slot="root"
        data-state="empty"
        data-variant={resolvedVariant}
        data-density={statusStates.density}
        className={cn(
          theme.slots.stateRoot,
          densityClassName,
          resolvedVariant === 'compact' && 'min-h-0 gap-1 py-3',
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.stateRoot,
          ...style,
        }}
      >
        {hasRenderableNode(icon) ? (
          <div
            data-slot="icon"
            data-state="empty"
            className={cn(theme.slots.stateIcon, classNames?.icon)}
            style={theme.styles.stateIcon}
          >
            {icon}
          </div>
        ) : null}
        {hasRenderableNode(title) ? (
          <div
            data-slot="title"
            data-state="empty"
            className={cn(theme.slots.stateTitle, classNames?.title)}
          >
            {title}
          </div>
        ) : null}
        {hasRenderableNode(description) ? (
          <div
            data-slot="description"
            data-state="empty"
            className={cn(theme.slots.stateDescription, classNames?.description)}
          >
            {description}
          </div>
        ) : null}
        {hasRenderableNode(actions) ? (
          <div
            data-slot="actions"
            data-state="empty"
            className={cn(theme.slots.stateActions, classNames?.actions)}
          >
            {actions}
          </div>
        ) : null}
      </div>
    );
  },
);
